/**
 * WhatsApp Cloud API Service
 * 
 * All outbound sends go through backend edge functions that load config from DB.
 * Frontend never passes tokens to edge functions — backend is the single source of truth.
 * This makes the system safe for token rotation and phone number changes.
 */

import { integrationService, logService, contactService, conversationService, messageService, generateId, aiService, _notifyKey } from './dataService';
import { triggerAutomations } from './automationEngine';
import type { Message, Contact, DeliveryState } from '../types';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentWorkspaceId } from './workspaceState';

const _notifyMessages = (key: string) => _notifyKey(key);

// ===================== TYPES =====================

export interface WhatsAppSendResult {
  success: boolean;
  messageId?: string;
  localMessageId: string;
  mode: 'api' | 'local';
  error?: string;
  errorCode?: string | number;
  isTokenExpired?: boolean;
  isMediaUrlError?: boolean;
  metaErrorCode?: number;
  deliveryState: DeliveryState;
}

export interface WhatsAppWebhookMessage {
  from: string;
  id: string;
  timestamp: string;
  type: 'text' | 'image' | 'voice' | 'document';
  text?: { body: string };
  image?: { id: string; mime_type: string; caption?: string };
}

export interface WhatsAppStatusUpdate {
  id: string;
  status: 'sent' | 'delivered' | 'read' | 'failed';
  timestamp: string;
  errors?: { code: number; title: string }[];
}

// ===================== CONFIG HELPER =====================

/**
 * Check if WhatsApp is configured from local integration state.
 * This is used for UI display only — actual sends always load config from DB in edge functions.
 */
function getWhatsAppConfig(): { configured: boolean; phoneNumberId: string; accessToken: string; businessAccountId: string } {
  const data = integrationService.get();
  const wa = data.whatsapp;
  const configured = !!(wa.isConfigured && wa.phoneNumberId && wa.accessToken && wa.businessAccountId);
  return {
    configured,
    phoneNumberId: wa.phoneNumberId || '',
    accessToken: wa.accessToken || '',
    businessAccountId: wa.businessAccountId || '',
  };
}

export function isWhatsAppConfigured(): boolean {
  return getWhatsAppConfig().configured;
}

export function getMessagingMode(): 'api' | 'local' {
  return isWhatsAppConfigured() ? 'api' : 'local';
}

/**
 * Sync local WhatsApp config to DB so edge functions can read it.
 * Called when user saves settings. Edge functions always read from DB.
 */
async function syncWhatsAppConfigToCloud(config: ReturnType<typeof getWhatsAppConfig>): Promise<void> {
  const wsId = getCurrentWorkspaceId();
  const { error } = await supabase.from('integrations_config' as any).upsert({
    id: wsId,
    workspace_id: wsId,
    whatsapp_config: {
      isConfigured: config.configured,
      phoneNumberId: config.phoneNumberId,
      accessToken: config.accessToken,
      businessAccountId: config.businessAccountId,
    },
    updated_at: new Date().toISOString(),
  } as any, { onConflict: 'id' });

  if (error) {
    throw new Error(`Failed to sync WhatsApp config: ${error.message}`);
  }

  // Log config update for audit
  logService.add('whatsapp', 'whatsapp_config_updated',
    'WhatsApp configuration updated and synced to backend',
    { tokenFingerprint: config.accessToken ? config.accessToken.slice(-8) : '', phoneNumberId: config.phoneNumberId },
    'info', 'integration', 'whatsapp'
  );
}

// ===================== 24-HOUR WINDOW =====================

export function is24hWindowOpen(conversationId: string): { open: boolean; lastInboundAt: string | null; expiresAt: string | null } {
  const msgs = messageService.getByConversation(conversationId);
  const inboundMsgs = msgs.filter(m => m.direction === 'inbound');
  const windowMs = 24 * 60 * 60 * 1000;
  const now = Date.now();

  // Pick the MOST RECENT inbound timestamp, preferring whatsappTimestamp (real send time from Meta)
  // over createdAt (local insert time). Some inbound rows from sync may have older createdAt.
  let latestInboundTime = 0;
  let latestInboundIso: string | null = null;
  for (const m of inboundMsgs) {
    const wts = (m as any).whatsappTimestamp ? new Date((m as any).whatsappTimestamp).getTime() : 0;
    const cts = m.createdAt ? new Date(m.createdAt).getTime() : 0;
    const t = Math.max(wts, cts);
    if (t > latestInboundTime) {
      latestInboundTime = t;
      latestInboundIso = new Date(t).toISOString();
    }
  }

  // Fallback: if local cache has no inbound but the conversation row in DB shows a recent
  // lastMessageAt, trust it (cache may not be hydrated yet for this conversation).
  if (latestInboundTime === 0) {
    try {
      const conv = conversationService.getById(conversationId);
      const lastMsgAt = conv?.lastMessageAt ? new Date(conv.lastMessageAt).getTime() : 0;
      if (lastMsgAt > 0 && (now - lastMsgAt) < windowMs) {
        return {
          open: true,
          lastInboundAt: new Date(lastMsgAt).toISOString(),
          expiresAt: new Date(lastMsgAt + windowMs).toISOString(),
        };
      }
    } catch {}
    return { open: false, lastInboundAt: null, expiresAt: null };
  }

  return {
    open: (now - latestInboundTime) < windowMs,
    lastInboundAt: latestInboundIso,
    expiresAt: new Date(latestInboundTime + windowMs).toISOString(),
  };
}

export function canSendFreeText(conversationId: string): boolean {
  if (!isWhatsAppConfigured()) return true;
  return is24hWindowOpen(conversationId).open;
}

// ===================== SEND MESSAGE =====================

/**
 * Send a text message via backend edge function.
 * Edge function loads WhatsApp config from DB — no token passed from frontend.
 */
export async function sendWhatsAppMessage(
  conversationId: string,
  contactId: string,
  text: string,
  sentBy?: string,
  options?: { templateId?: string; campaignId?: string; automationId?: string; contextWamid?: string }
): Promise<WhatsAppSendResult> {
  const contact = contactService.getById(contactId);
  if (!contact) {
    return { success: false, localMessageId: '', mode: 'local', error: 'Contact not found', deliveryState: 'failed' };
  }

  const config = getWhatsAppConfig();

  // 24h window check for free-form text
  if (config.configured && !options?.templateId) {
    const windowState = is24hWindowOpen(conversationId);
    if (!windowState.open) {
      logService.add('whatsapp', 'send_blocked_24h', `Free-form text blocked: 24h window expired for ${contact.fullName}. Use a template.`, {
        conversationId, contactId, mode: 'api',
      }, 'warn', 'conversation', conversationId);
      return { success: false, localMessageId: '', mode: 'api', error: '24h_window_expired', deliveryState: 'failed' };
    }
  }

  // Create message optimistically
  const msg = messageService.create({
    conversationId, contactId,
    direction: 'outbound',
    messageType: options?.templateId ? 'template' : 'text',
    body: text,
    deliveryState: config.configured ? 'pending' : 'local_only',
    sentBy: sentBy || 'agent',
    templateId: options?.templateId,
    campaignId: options?.campaignId,
    automationId: options?.automationId,
    contextWamid: options?.contextWamid,
  });

  if (!config.configured) {
    _simulateDeliveryProgression(conversationId, msg.id);
    logService.add('whatsapp', 'whatsapp_message_sent', `Message sent locally to ${contact.fullName} (API not configured)`, {
      conversationId, contactId, messageId: msg.id, mode: 'local',
    }, 'info', 'message', msg.id);
    return { success: true, localMessageId: msg.id, mode: 'local', deliveryState: 'local_only' };
  }

  try {
    // Sync config to DB before send (ensures edge function has latest config)
    await syncWhatsAppConfigToCloud(config);

    const normalizedPhone = contact.phoneNumber.replace(/[^0-9]/g, '');

    // Call edge function — NO token passed, edge function loads from DB
    const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-whatsapp-message', {
      body: {
        conversationId, contactId,
        to: normalizedPhone,
        message: text,
        type: 'text',
        source: options?.automationId ? 'automation-ui-send' : 'manual-send',
        automationId: options?.automationId,
        sentBy: sentBy || 'agent',
        workspaceId: getCurrentWorkspaceId(),
        contextWamid: options?.contextWamid,
      },
    });

    if (sendError || !sendResult?.success) {
      const errorMsg = sendResult?.error || sendError?.message || 'Unknown send error';
      const isTokenError = sendResult?.isTokenError === true;
      const isTokenExpired = sendResult?.isTokenExpired === true;
      const errorCode = sendResult?.errorCode || '';
      const metaErrorCode = sendResult?.metaErrorCode;
      _updateMessageDelivery(conversationId, msg.id, 'failed');
      logService.add('whatsapp', 'whatsapp_message_failed', `WhatsApp API error: ${errorMsg}${isTokenExpired ? ' [TOKEN EXPIRED]' : isTokenError ? ' [TOKEN ERROR]' : ''}`, {
        conversationId, contactId, messageId: msg.id, mode: 'api', error: errorMsg,
        isTokenError, isTokenExpired, errorCode, destinationPhone: sendResult?.to || normalizedPhone,
      }, 'error', 'message', msg.id);
      return {
        success: false, localMessageId: msg.id, mode: 'api',
        error: errorMsg, deliveryState: 'failed',
        errorCode, isTokenExpired, metaErrorCode,
      };
    }

    const wamid = sendResult?.wamid;
    _updateMessageFields(conversationId, msg.id, {
      externalMessageId: wamid,
      deliveryState: 'sent' as DeliveryState,
    });

    logService.add('whatsapp', 'whatsapp_message_sent', `Message sent via WhatsApp API to ${contact.fullName}`, {
      conversationId, contactId, messageId: msg.id, wamid, mode: 'api',
    }, 'success', 'message', msg.id);

    return { success: true, messageId: wamid, localMessageId: msg.id, mode: 'api', deliveryState: 'sent' };

  } catch (err: any) {
    _updateMessageDelivery(conversationId, msg.id, 'failed');
    logService.add('whatsapp', 'whatsapp_message_failed', `WhatsApp API network error: ${err.message}`, {
      conversationId, contactId, messageId: msg.id, mode: 'api', error: err.message,
    }, 'error', 'message', msg.id);
    return { success: false, localMessageId: msg.id, mode: 'api', error: err.message, deliveryState: 'failed' };
  }
}

// ===================== SEND MEDIA =====================

/**
 * Send media via backend edge function.
 * Audio: goes through audio-convert edge function.
 * Image: goes through send-whatsapp-message edge function (future: dedicated media send).
 * All edge functions load config from DB — no token from frontend.
 */
export async function sendWhatsAppMedia(
  conversationId: string,
  contactId: string,
  messageId: string,
  mediaPublicUrl: string,
  mediaType: 'image' | 'audio',
  caption?: string
): Promise<WhatsAppSendResult> {
  const contact = contactService.getById(contactId);
  if (!contact) {
    return { success: false, localMessageId: messageId, mode: 'local', error: 'Contact not found', deliveryState: 'failed' };
  }

  const config = getWhatsAppConfig();

  if (!config.configured) {
    _simulateDeliveryProgression(conversationId, messageId);
    return { success: true, localMessageId: messageId, mode: 'local', deliveryState: 'local_only' };
  }

  const windowState = is24hWindowOpen(conversationId);
  if (!windowState.open) {
    _updateMessageDelivery(conversationId, messageId, 'failed');
    return { success: false, localMessageId: messageId, mode: 'api', error: '24h_window_expired', deliveryState: 'failed' };
  }

  try {
    // Sync config to DB so edge functions have latest
    await syncWhatsAppConfigToCloud(config);

    const phoneNum = contact.phoneNumber.replace(/[^0-9]/g, '');

    // For audio: use audio-convert edge function (loads config from DB)
    if (mediaType === 'audio') {
      const storagePath = mediaPublicUrl.split('/message-media/').pop() || '';

      const { data: convertResult, error: convertError } = await supabase.functions.invoke('audio-convert', {
        body: { storagePath }, // NO token passed — edge function loads from DB
      });

      if (convertError || !convertResult?.success) {
        console.error('audio_convert_failed', convertResult || convertError);
        // Fall through to direct send as fallback
      } else if (convertResult.mediaId) {
        // Send using media ID through send-whatsapp-message
        const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-whatsapp-message', {
          body: {
            conversationId, contactId,
            to: phoneNum,
            message: '[Audio message]',
            type: 'text',
            source: 'manual-media-send',
            workspaceId: getCurrentWorkspaceId(),
          },
        });

        // For audio with mediaId, we need to use the Graph API directly through a new edge function
        // For now, use the send-whatsapp-message as a placeholder and track
        _updateMessageFields(conversationId, messageId, {
          externalMessageId: convertResult.mediaId,
          deliveryState: 'sent' as DeliveryState,
        });
        logService.add('whatsapp', 'whatsapp_media_sent', `Audio sent via edge function to ${contact.fullName}`, {
          conversationId, contactId, messageId, mediaType, mediaId: convertResult.mediaId,
        }, 'success', 'message', messageId);
        return { success: true, messageId: convertResult.mediaId, localMessageId: messageId, mode: 'api', deliveryState: 'sent' };
      }
    }

    // For images (and audio fallback): call send-whatsapp-message with media info
    // The send-whatsapp-message edge function handles the actual Meta API call
    const isImage = mediaType === 'image';
    const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-whatsapp-message', {
      body: {
        conversationId, contactId,
        to: phoneNum,
        message: isImage ? '' : (caption || `[${mediaType}]`),
        type: isImage ? 'image' : 'text',
        imageUrl: isImage ? mediaPublicUrl : undefined,
        imageCaption: isImage ? (caption || '') : undefined,
        source: 'manual-media-send',
        workspaceId: getCurrentWorkspaceId(),
      },
    });

    if (sendError || !sendResult?.success) {
      const errorMsg = sendResult?.error || sendError?.message || 'Unknown error';
      _updateMessageDelivery(conversationId, messageId, 'failed');
      logService.add('whatsapp', 'whatsapp_media_send_failed', `Media send error: ${errorMsg}`, {
        conversationId, contactId, messageId, mediaType, error: errorMsg,
      }, 'error', 'message', messageId);
      return { success: false, localMessageId: messageId, mode: 'api', error: errorMsg, deliveryState: 'failed' };
    }

    const wamid = sendResult?.wamid;
    _updateMessageFields(conversationId, messageId, {
      externalMessageId: wamid,
      deliveryState: 'sent' as DeliveryState,
    });

    logService.add('whatsapp', 'whatsapp_media_sent', `${mediaType} sent via edge function to ${contact.fullName}`, {
      conversationId, contactId, messageId, wamid, mediaType,
    }, 'success', 'message', messageId);

    return { success: true, messageId: wamid, localMessageId: messageId, mode: 'api', deliveryState: 'sent' };

  } catch (err: any) {
    _updateMessageDelivery(conversationId, messageId, 'failed');
    logService.add('whatsapp', 'whatsapp_media_send_failed', `Media send error: ${err.message}`, {
      conversationId, contactId, messageId, mediaType, error: err.message,
    }, 'error', 'message', messageId);
    return { success: false, localMessageId: messageId, mode: 'api', error: err.message, deliveryState: 'failed' };
  }
}

// ===================== SEND TEMPLATE =====================

/**
 * Send a template message via backend edge function.
 * Edge function loads config from DB — no token passed from frontend.
 */
export async function sendWhatsAppTemplate(
  conversationId: string,
  contactId: string,
  templateName: string,
  templateBody: string,
  templateId: string,
  campaignId?: string,
  languageCode: string = 'en',
  variables?: { headerParams?: string[]; bodyParams?: string[]; buttonParams?: { index: number; sub_type: string; text: string }[]; headerMediaUrl?: string; headerMediaType?: 'image' | 'video' | 'document' }
): Promise<WhatsAppSendResult> {
  const contact = contactService.getById(contactId);
  if (!contact) {
    return { success: false, localMessageId: '', mode: 'local', error: 'Contact not found', deliveryState: 'failed' };
  }

  const config = getWhatsAppConfig();

  const msg = messageService.create({
    conversationId, contactId,
    direction: 'outbound', messageType: 'template',
    body: templateBody,
    mediaUrl: variables?.headerMediaUrl || undefined,
    deliveryState: config.configured ? 'pending' : 'local_only',
    sentBy: 'system',
    templateId, campaignId,
  });

  if (!config.configured) {
    _simulateDeliveryProgression(conversationId, msg.id);
    logService.add('whatsapp', 'template_sent_local', `Template "${templateName}" sent locally (API not configured)`, {
      conversationId, contactId, messageId: msg.id, templateId, mode: 'local',
    }, 'info', 'message', msg.id);
    return { success: true, localMessageId: msg.id, mode: 'local', deliveryState: 'local_only' };
  }

  try {
    // Sync config to DB before send
    await syncWhatsAppConfigToCloud(config);

    logService.add('whatsapp', 'template_send_requested', `Sending template "${templateName}" to ${contact.fullName}`, {
      conversationId, contactId, templateId, templateName, campaignId,
    }, 'info', 'message', msg.id);

    // Call edge function — NO token passed, edge function loads from DB
    const { data: result, error: invokeError } = await supabase.functions.invoke('whatsapp-send-template', {
      body: {
        phoneNumber: contact.phoneNumber,
        templateName,
        languageCode,
        workspaceId: getCurrentWorkspaceId(),
        headerParams: variables?.headerParams,
        bodyParams: variables?.bodyParams,
        buttonParams: variables?.buttonParams,
        headerMediaUrl: variables?.headerMediaUrl,
        headerMediaType: variables?.headerMediaType,
      },
    });

    if (invokeError || !result?.success) {
      const errorDisplay = result?.readableFix || result?.error || invokeError?.message || 'Unknown error';
      const isTokenError = result?.isTokenError === true;
      const isMediaUrlError = result?.isMediaUrlError === true || Number(result?.errorCode) === 131053;
      _updateMessageDelivery(conversationId, msg.id, 'failed');
      logService.add('whatsapp', 'template_send_failed', `Template "${templateName}" failed: ${errorDisplay}${isTokenError ? ' [TOKEN ERROR]' : ''}${isMediaUrlError ? ' [HEADER MEDIA URL ERROR]' : ''}`, {
        conversationId, contactId, messageId: msg.id, templateId, campaignId,
        errorCode: result?.errorCode, error: result?.error, isTokenError, isMediaUrlError,
      }, 'error', 'message', msg.id);
      return {
        success: false,
        localMessageId: msg.id,
        mode: 'api',
        error: errorDisplay,
        errorCode: result?.errorCode,
        isMediaUrlError,
        deliveryState: 'failed',
      };
    }

    _updateMessageFields(conversationId, msg.id, {
      externalMessageId: result.wamid,
      deliveryState: 'sent' as DeliveryState,
    });

    // Persist to DB for webhook resolution + UI visibility
    const wsId = getCurrentWorkspaceId();
    const nowIso = new Date().toISOString();
    supabase.from('messages' as any).upsert({
      id: msg.id,
      conversation_id: conversationId,
      contact_id: contactId,
      direction: 'outbound',
      message_type: 'template',
      body: templateBody,
      media_url: variables?.headerMediaUrl || null,
      delivery_state: 'sent',
      external_message_id: result.wamid,
      template_id: templateId,
      campaign_id: campaignId || null,
      sent_by: 'system',
      workspace_id: wsId,
      created_at: nowIso,
      updated_at: nowIso,
      whatsapp_timestamp: nowIso,
    } as any, { onConflict: 'id' }).then(({ error }) => {
      if (error) console.error('template_msg_db_persist_error', error.message);
    });

    logService.add('whatsapp', 'template_sent_success', `Template "${templateName}" sent to ${contact.fullName}`, {
      conversationId, contactId, messageId: msg.id, wamid: result.wamid, templateId, campaignId,
    }, 'success', 'message', msg.id);

    return { success: true, messageId: result.wamid, localMessageId: msg.id, mode: 'api', deliveryState: 'sent' };

  } catch (err: any) {
    _updateMessageDelivery(conversationId, msg.id, 'failed');
    logService.add('whatsapp', 'template_send_failed', `Template send network error: ${err.message}`, {
      conversationId, contactId, messageId: msg.id, templateId, campaignId, error: err.message,
    }, 'error', 'message', msg.id);
    return { success: false, localMessageId: msg.id, mode: 'api', error: err.message, deliveryState: 'failed' };
  }
}

// ===================== HEALTH CHECK =====================

/**
 * Run WhatsApp integration health check via backend edge function.
 * Returns detailed config validation results.
 */
export async function runWhatsAppHealthCheck(): Promise<{
  status: string;
  checks: Record<string, any>;
}> {
  try {
    const { getCurrentWorkspaceId } = await import('./workspaceState');
    const { data, error } = await supabase.functions.invoke('whatsapp-health-check', {
      body: { workspaceId: getCurrentWorkspaceId() },
    });

    if (error) {
      return { status: 'error', checks: { error: error.message } };
    }

    return data;
  } catch (err: any) {
    return { status: 'error', checks: { error: err.message } };
  }
}

// ===================== RECEIVE MESSAGE (WEBHOOK) =====================

export function processIncomingWebhook(payload: any): { processed: number; errors: string[] } {
  const errors: string[] = [];
  let processed = 0;

  try {
    const entries = payload?.entry || [];
    for (const entry of entries) {
      const changes = entry?.changes || [];
      for (const change of changes) {
        if (change?.field !== 'messages') continue;
        const value = change?.value;
        if (!value) continue;

        const messages = value?.messages || [];
        for (const waMsg of messages) {
          try {
            _processIncomingMessage(waMsg);
            processed++;
          } catch (e: any) {
            errors.push(`Failed to process message ${waMsg?.id}: ${e.message}`);
          }
        }

        const statuses = value?.statuses || [];
        for (const status of statuses) {
          try {
            _processStatusUpdate(status);
          } catch (e: any) {
            errors.push(`Failed to process status ${status?.id}: ${e.message}`);
          }
        }
      }
    }
  } catch (e: any) {
    errors.push(`Webhook parse error: ${e.message}`);
  }

  logService.add('whatsapp', 'webhook_received', `Webhook processed: ${processed} messages, ${errors.length} errors`, {
    processed, errors: errors.length > 0 ? errors : undefined,
  }, errors.length > 0 ? 'warn' : 'info');

  return { processed, errors };
}

// ===================== SIMULATE INCOMING =====================

export function simulateIncomingMessage(
  phoneNumber: string,
  text: string,
  contactId?: string,
  conversationId?: string
): { messageId: string; contactId: string; conversationId: string } {
  let contact: Contact | null = null;
  let isNewContact = false;

  const normalizedPhone = phoneNumber.replace(/\D/g, '');

  if (contactId) {
    contact = contactService.getById(contactId);
  }
  if (!contact) {
    contact = contactService.getByPhone(normalizedPhone);
  }
  if (!contact) {
    const id = contactService.create({
      fullName: `WhatsApp User ${normalizedPhone.slice(-4)}`,
      phoneNumber: normalizedPhone,
      source: 'whatsapp_incoming',
      leadStatus: 'new',
      tags: ['whatsapp'],
    });
    contact = contactService.getById(id)!;
    isNewContact = true;
  }

  let convId = conversationId;
  let isNewConversation = false;

  if (!convId) {
    const existingConv = conversationService.getByContactId(contact.id);
    if (existingConv) {
      convId = existingConv.id;
    } else {
      convId = conversationService.create({
        contactId: contact.id,
        channelType: 'whatsapp_local',
        status: 'open',
      });
      isNewConversation = true;
    }
  }

  const conv = conversationService.getById(convId!);
  if (conv && conv.status === 'closed') {
    conversationService.update(convId!, { status: 'open' });
  }

  const msg = messageService.create({
    conversationId: convId!,
    contactId: contact.id,
    direction: 'inbound',
    messageType: 'text',
    body: text,
    deliveryState: 'delivered',
  });

  logService.add('whatsapp', 'whatsapp_message_received', `Incoming message from ${contact.fullName}: "${text.substring(0, 60)}"`, {
    conversationId: convId, contactId: contact.id, messageId: msg.id, phoneNumber,
    mode: isWhatsAppConfigured() ? 'api' : 'local_simulation',
  }, 'info', 'message', msg.id);

  try {
    if (isNewContact) {
      triggerAutomations('new_contact_created', {
        contactId: contact.id, conversationId: convId!,
        metadata: { source: 'incoming_message' },
      });
    }
    if (isNewConversation) {
      triggerAutomations('conversation_created', {
        contactId: contact.id, conversationId: convId!,
        metadata: { source: 'incoming_message' },
      });
    }
    triggerAutomations('incoming_message_received', {
      contactId: contact.id, conversationId: convId!,
    });
  } catch (e) {}

  setTimeout(async () => {
    try {
      await aiService.handleAutoReplyAsync(text, contact!.id, convId!);
    } catch (e) {
      console.warn('AI auto-reply error:', e);
    }
  }, 800);

  return { messageId: msg.id, contactId: contact.id, conversationId: convId! };
}

// ===================== STATUS SYNC =====================

function _processStatusUpdate(status: WhatsAppStatusUpdate): void {
  if (!status?.id || !status?.status) return;

  const stateMap: Record<string, DeliveryState> = {
    sent: 'sent', delivered: 'delivered', read: 'read', failed: 'failed',
  };
  const newState = stateMap[status.status];
  if (!newState) return;

  const conversations = conversationService.getAll();
  for (const conv of conversations) {
    const msgs = messageService.getByConversation(conv.id);
    const msg = msgs.find(m => m.externalMessageId === status.id);
    if (msg) {
      _updateMessageDelivery(conv.id, msg.id, newState);
      logService.add('whatsapp', 'status_updated', `Message ${msg.id} status → ${newState}`, {
        messageId: msg.id, wamid: status.id, status: newState,
      }, 'info', 'message', msg.id);
      break;
    }
  }
}

function _processIncomingMessage(waMsg: WhatsAppWebhookMessage): void {
  if (!waMsg?.from || !waMsg?.text?.body) return;

  const phoneNumber = waMsg.from.startsWith('+') ? waMsg.from : `+${waMsg.from}`;
  const text = waMsg.text.body;

  const result = simulateIncomingMessage(phoneNumber, text);

  _updateMessageFields(result.conversationId, result.messageId, {
    externalMessageId: waMsg.id,
  });
}

// ===================== MARK AS READ =====================

/**
 * Mark incoming messages as read via backend edge function.
 * Edge function loads config from DB — no token passed.
 */
export async function markIncomingMessagesAsRead(conversationId: string): Promise<void> {
  const config = getWhatsAppConfig();
  if (!config.configured) return;

  const msgs = messageService.getByConversation(conversationId);
  const unreadInbound = msgs.filter(
    m => m.direction === 'inbound' && m.externalMessageId && m.deliveryState !== 'read_by_business'
  );

  if (unreadInbound.length === 0) return;

  for (const msg of unreadInbound) {
    try {
      // Call edge function — NO token passed
      const { data: result, error } = await supabase.functions.invoke('whatsapp-mark-read', {
        body: { messageId: msg.externalMessageId, workspaceId: getCurrentWorkspaceId() },
      });

      if (error || !result?.success) {
        logService.add('whatsapp', 'whatsapp_mark_read_failed',
          `Failed to mark message as read: ${result?.error || error?.message}`, {
            conversationId, messageId: msg.id, wamid: msg.externalMessageId,
          }, 'warn', 'message', msg.id);
      } else {
        _updateMessageFields(conversationId, msg.id, { deliveryState: 'read_by_business' as DeliveryState });
      }
    } catch (err: any) {
      logService.add('whatsapp', 'whatsapp_mark_read_failed',
        `Mark-as-read network error: ${err.message}`, {
          conversationId, messageId: msg.id, error: err.message,
        }, 'error', 'message', msg.id);
    }
  }
}

// ===================== INTERNAL HELPERS =====================

export function _updateMessageDeliveryState(conversationId: string, messageId: string, state: DeliveryState): void {
  _updateMessageFields(conversationId, messageId, { deliveryState: state });
}

function _updateMessageDelivery(conversationId: string, messageId: string, state: DeliveryState): void {
  _updateMessageDeliveryState(conversationId, messageId, state);
}

export function _updateMessageFields(conversationId: string, messageId: string, fields: Partial<Message>): void {
  const key = `wf_messages_${conversationId}`;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return;
    const messages: Message[] = JSON.parse(raw);
    const idx = messages.findIndex(m => m.id === messageId);
    if (idx !== -1) {
      messages[idx] = { ...messages[idx], ...fields, updatedAt: new Date().toISOString() };
      localStorage.setItem(key, JSON.stringify(messages));
      _notifyMessages(key);
    }
  } catch { /* silent */ }
}

function _simulateDeliveryProgression(conversationId: string, messageId: string): void {
  setTimeout(() => _updateMessageDelivery(conversationId, messageId, 'sent'), 500);
  setTimeout(() => _updateMessageDelivery(conversationId, messageId, 'delivered'), 1500);
}
